import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useSelector } from 'react-redux';
import { selectCurrentRole } from '../redux/globalDataSlice';

import {
  ArrowRightStartOnRectangleIcon,
  ChatBubbleBottomCenterTextIcon,
  ClipboardDocumentListIcon,
  HomeIcon,
  UserGroupIcon,
  ServerStackIcon,
  NewspaperIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

import AccountPicker from './AccountPicker';

const canAccess = (item, role) =>
  !item.roles || item.roles.includes(role);

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon, matchExact: true },
  { name: 'Connectors', href: '/connectors', icon: UserGroupIcon, relatedPaths: ['/add-connector', '/edit-connector'],roles: ['ADMIN', 'DEVELOPER'] },
  {
    name: 'Opt-Out',
    icon: ArrowRightStartOnRectangleIcon,
    children: [
      {
        name: 'Opt-Out Search',
        href: '/optouts', // your new page
        relatedPaths: ['/edit-contact', '/add-contact'], // adjust if needed
      },
      {
        name: 'Opt-Out Policies',
        href: '/optin-optout-policies',
        relatedPaths: ['/add-optin-optout-policy', '/edit-optin-optout-policy'],
        roles: ['ADMIN', 'DEVELOPER']
      },
    ],
  },
  { name: 'Senders', href: '/senders', icon: ServerStackIcon, relatedPaths: ['/add-sender', '/edit-sender'],roles: ['ADMIN', 'DEVELOPER'] },
  { name: 'Use Cases', href: '/templates', icon: ChatBubbleBottomCenterTextIcon, relatedPaths: ['/add-template', '/edit-template'], roles: ['ADMIN', 'DEVELOPER']
  },
  { name: 'Logging', href: '/logging', icon: NewspaperIcon,roles: ['ADMIN', 'DEVELOPER', 'SUPPORT'] },
];

const adminOnlyNav = [
  { name: 'Manage Users', href: '/users', icon: UsersIcon, roles: ['ADMIN', 'DEVELOPER'] },
];

const helpNav = [
  { id: 1, name: 'Help', href: '#', initial: 'H' },
  { id: 2, name: 'Support', href: '#', initial: 'S' },
  { id: 3, name: 'Documentation', href: '#', initial: 'D' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Sidebar = () => {
  const { pathname } = useLocation();
  const role = useSelector(selectCurrentRole);
  const visibleNavigation = navigation.filter(item => canAccess(item, role));

  const isLeafActive = (item) => {
    if (item.matchExact) {
      return pathname?.toLowerCase() === item.href?.toLowerCase();
    }
    if (item.relatedPaths) {
      return (
        item.relatedPaths.some((p) => pathname?.toLowerCase().startsWith(p.toLowerCase())) ||
        pathname?.toLowerCase() === item.href?.toLowerCase()
      );
    }
    return pathname?.toLowerCase() === item.href?.toLowerCase();
  };

  // A parent is active if any child is active
  const isParentActive = (parent) =>
    Array.isArray(parent.children) && parent.children.some(isLeafActive);

  // Initial open state: open any group that has an active child
  const initialOpenGroups = useMemo(() => {
    const state = {};
    navigation.forEach((item) => {
      if (item.children) state[item.name] = isParentActive(item);
    });
    return state;
  }, [pathname]);

  const [openGroups, setOpenGroups] = useState(initialOpenGroups);
  const toggleGroup = (name) =>
    setOpenGroups((s) => ({ ...s, [name]: !s[name] }));

  const manageUsersActive =
    pathname?.toLowerCase() === '/users' ||
    pathname?.toLowerCase().startsWith('/users');

  return (
    <div className="fixed inset-y-0 z-50 hidden w-72 flex-col lg:flex">
      <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gradient-to-t from-black dark:from-gray-800 to-gray-600 dark:to-black dark:border-r dark:border-gray-800 px-6 pb-4">
        <div className="flex h-16 shrink-0 items-center">
          <Link to="/">
            <img alt="UBIQIO" src="ubiqio-logo.png" className="h-8 w-auto" />
          </Link>
        </div>

        {/* Account dropdown */}
        <AccountPicker />

        <nav className="flex flex-1 flex-col">
          <ul role="list" className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul role="list" className="-mx-2 space-y-1">
              {visibleNavigation.map((item, index) => {
                  // Render leaf items (no children)
                  if (!item.children) {
                    const active = isLeafActive(item);
                    return (
                      <li key={index}>
                        <Link
                          to={item.href}
                          className={classNames(
                            active
                              ? 'bg-emerald-400 dark:bg-emerald-600 text-black dark:text-white'
                              : 'text-gray-400 dark:text-gray-400 hover:bg-gray-800 dark:hover:bg-gray-200 hover:text-white dark:hover:text-black',
                            'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                          )}
                        >
                          {item.icon && <item.icon aria-hidden="true" className="size-6 shrink-0" />}
                          {item.name}
                        </Link>
                      </li>
                    );
                  }

                  // Render parent with children (collapsible)
                  const parentActive = isParentActive(item);
                  const open = openGroups[item.name] ?? false;

                  return (
                    <li key={index}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.name)}
                        className={classNames(
                          parentActive
                            ? 'bg-emerald-400/20 dark:bg-emerald-600/20 text-white'
                            : 'text-gray-300 dark:text-gray-400 hover:bg-gray-800 dark:hover:bg-gray-200 hover:text-white dark:hover:text-black',
                          'w-full group flex items-center gap-x-3 rounded-md p-2 text-left text-sm/6 font-semibold'
                        )}
                      >
                        {item.icon && <item.icon aria-hidden="true" className="size-6 shrink-0" />}
                        <span className="flex-1">{item.name}</span>
                        <ChevronUpDownIcon
                          aria-hidden="true"
                          className={classNames(
                            'size-5 shrink-0 transition-transform',
                            open ? 'rotate-180' : 'rotate-0'
                          )}
                        />
                      </button>

                      {open && (
                        <ul role="list" className="mt-1 ml-9 space-y-1">
                        {item.children
                            .filter(child => canAccess(child, role))  // 👈 add this
                            .map((child, cIdx) => {
                              const active = isLeafActive(child);
                            return (
                              <li key={cIdx}>
                                <Link
                                  to={child.href}
                                  className={classNames(
                                    active
                                      ? 'bg-emerald-400 dark:bg-emerald-600 text-black dark:text-white'
                                      : 'text-gray-400 dark:text-gray-400 hover:bg-gray-800 dark:hover:bg-gray-200 hover:text-white dark:hover:text-black',
                                    'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold'
                                  )}
                                >
                                  {/* small disc marker */}
                                  <span className={classNames(
                                    'mt-2 size-2 rounded-full border',
                                    active
                                      ? 'bg-emerald-300 border-emerald-500'
                                      : 'bg-gray-500 border-gray-400'
                                  )} />
                                  {child.name}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>

            {/* <li>
              <div className="text-xs/6 font-semibold text-gray-300 dark:text-gray-400">Need help?</div>
              <ul role="list" className="-mx-2 mt-2 space-y-1">
                {helpNav.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={item.href}
                      className="text-gray-400 dark:text-gray-400 hover:bg-gray-800 dark:hover:bg-gray-200 hover:text-white dark:hover:text-black group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                    >
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-lg border border-gray-700 dark:border-gray-300 bg-gray-800 dark:bg-gray-200 text-[0.625rem] font-medium text-gray-400 dark:text-gray-400 group-hover:text-white dark:group-hover:text-black">
                        {item.initial}
                      </span>
                      <span className="truncate">{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </li> */}

            <li className="mt-auto">
              <div className="space-y-1">
                {/* Audit Log — visible to all roles */}
                {(() => {
                  const auditActive =
                    pathname?.toLowerCase() === '/audit-log' ||
                    pathname?.toLowerCase().startsWith('/audit-log');
                  return (
                    <Link
                      to="/audit-log"
                      className={classNames(
                        auditActive
                          ? 'bg-emerald-400 dark:bg-emerald-600 text-black dark:text-white'
                          : 'text-gray-400 dark:text-gray-400 hover:bg-gray-800 dark:hover:bg-gray-200 hover:text-white dark:hover:text-black',
                        'group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold'
                      )}
                    >
                      <ClipboardDocumentListIcon aria-hidden="true" className="size-6 shrink-0" />
                      Audit Log
                    </Link>
                  );
                })()}

                {/* Manage Users — ADMIN / DEVELOPER only */}
                {canAccess({ roles: ['ADMIN', 'DEVELOPER'] }, role) && (
                  <Link
                    to="/users"
                    className={classNames(
                      manageUsersActive
                        ? 'bg-emerald-400 dark:bg-emerald-600 text-black dark:text-white'
                        : 'text-gray-400 dark:text-gray-400 hover:bg-gray-800 dark:hover:bg-gray-200 hover:text-white dark:hover:text-black',
                      'group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold'
                    )}
                  >
                    <UsersIcon aria-hidden="true" className="size-6 shrink-0" />
                    Manage Users
                  </Link>
                )}
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
